import React, { useEffect } from 'react';
import { colorHex } from '../../../../utils/validators';
import { TextInput, View } from 'react-native';
import { colors } from '../../../../utils/theme';
import Icon from '../../Icon';
import Stack from '../../Stack';
import Text from '../../Text';
import { innerTextInputStyles } from '../InputBase';
import ColorPicker from 'react-native-wheel-color-picker';
import { usePromptButton } from '../../../shared/PromptButton';
import { getIntl } from '../../../../utils/intl';
import PressableDynamicOpacity from '../../PressableDynamicOpacity';
import { useIsMobileLayout } from '../../../../hooks/useIsMobileLayout';

const intl = getIntl('ColorPickerInput', {
    colorPickerPrompt: 'Select a Color',
});

export type ColorPickerInputOwnProps = {
    error?: string;
    value: string | null;
    onChange: (value: string | null) => void;
    onBlur?: () => void;
    onFocus?: () => void;
    isDisabled?: boolean;
    testID?: string;
};

export type ColorPickerInputProps = ColorPickerInputOwnProps;

const CONSTANTS = {
    WIDTH: 110,
    HEIGHT: 30,
    TEXT_INPUT_WIDTH: 50,
};

const stripHash = (value: string) => value.replace(/^#/, '');
const maybeAddHash = (value: string) => (value.length === 6 && value[0] !== '#' ? `#${value}` : value);
const formatInputValue = (value: string | null): string | null => {
    if (!value) {
        return null;
    }
    // remove # from the beginning if it exists
    const trimmedValue = value.replace(/^#/, '');
    // clip to 6 characters
    const clippedValue = trimmedValue.slice(0, 6);
    // capitalize letters
    const upperCasedValue = clippedValue.toUpperCase();
    return upperCasedValue;
};

const ColorPickerInput: React.FC<ColorPickerInputProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const [internalValue, setInternalValue] = React.useState<string | null>(
        !props.value || colorHex(stripHash(props.value)) ? null : stripHash(props.value) || null,
    );
    useEffect(() => {
        // set parent to internal value if it is valid, otherwise clear it
        const newParentValue = !internalValue || colorHex(internalValue) ? null : maybeAddHash(internalValue);
        props.onChange(newParentValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [internalValue]);
    const handleTextChange = (value: string | null) => {
        setInternalValue(formatInputValue(value));
    };

    const colorPickerPrompt = usePromptButton({
        renderPromptContents: () => (
            <Stack
                vertical
                fullWidth
                justify="center"
                align="center"
                space={2}
                testID={`${props.testID}-picker-prompt`}
            >
                <ColorPicker
                    onColorChangeComplete={(color) => {
                        const formatted = formatInputValue(color);
                        setInternalValue(formatted);
                    }}
                    thumbSize={isMobile ? 40 : 20}
                />
            </Stack>
        ),
        promptTitle: intl('colorPickerPrompt'),
        onClosed: props.onBlur,
    });

    const displayError = colorHex(internalValue) || props.error;

    const renderPreview = () => {
        const renderInContainer = (color: string, children: React.ReactNode | null) => (
            <PressableDynamicOpacity
                onPress={() => {
                    props.onFocus?.();
                    colorPickerPrompt.onPress();
                }}
                testID={`${props.testID}-preview`}
            >
                <View
                    style={{
                        width: CONSTANTS.HEIGHT,
                        height: CONSTANTS.HEIGHT,
                        backgroundColor: color,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRightWidth: 1,
                        borderRightColor: colors.Dividers,
                    }}
                >
                    {children}
                </View>
            </PressableDynamicOpacity>
        );

        if (!props.value) {
            return renderInContainer(
                colors['Background'],
                <Icon color="Dark-Text" name="XSquare" size={'button-icon'} />,
            );
        } else {
            return renderInContainer(maybeAddHash(props.value), null);
        }
    };

    return (
        <>
            <View
                style={{
                    width: CONSTANTS.WIDTH,
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                }}
            >
                <View
                    style={{
                        width: CONSTANTS.WIDTH,
                        height: CONSTANTS.HEIGHT,
                        borderRadius: 6,
                        borderWidth: 1,
                        borderColor: displayError ? colors.Alert : colors.Dividers,
                        flexDirection: 'row',
                        alignItems: 'center',
                        overflow: 'hidden',
                    }}
                >
                    {renderPreview()}
                    <Stack horizontal justify="center" space={1} flex={1}>
                        <Text variant="body-1" color="Grey-Light">
                            {'#'}
                        </Text>
                        <TextInput
                            style={[innerTextInputStyles, { width: CONSTANTS.TEXT_INPUT_WIDTH }]}
                            onFocus={() => props.onFocus?.()}
                            onBlur={() => props.onBlur?.()}
                            value={internalValue || ''}
                            numberOfLines={1}
                            autoCorrect={false}
                            onChange={(e) => handleTextChange(e.nativeEvent.text)}
                            placeholder={'FFFFFF'}
                            placeholderTextColor={colors['Grey-Light']}
                            testID={`${props.testID}-input`}
                        />
                    </Stack>
                </View>
                {displayError ? (
                    <Text variant="input-error" style={{ marginTop: 4 }}>
                        {displayError}
                    </Text>
                ) : null}
            </View>
            {colorPickerPrompt.Prompt}
        </>
    );
};

export default React.memo(ColorPickerInput);
